import {useCallback, useState} from "react";
import {HttpMethod, initFetch} from "../../utils/fetcher";

/**
 * Hook personalisé pour la gestion du compte extranet
 *
 * @returns {{data: {name: string, email: string, lot_id: []}, loading: boolean, error: unknown, success: boolean, createCredentials: (function(*): Promise<*|undefined>), updateCredentials: (function(*, *): Promise<*|undefined>), readCredentials: (function(*): Promise<*|undefined>), reset: (function(): void)}}
 */
const useUserCredential = () => {

  const [data, setData] = useState({
    name: '',
    email: '',
    lots: []
  });

  const [loading,setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fonction pour créer de nouvelles données
  const createCredentials = useCallback(async (credentialsData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { url, params} = initFetch('extranet/user-credentials', HttpMethod.POST);
      const response = await fetch(url, {
        ...params,
        body: JSON.stringify({
          name: credentialsData.name,
          email: credentialsData.email,
          password: credentialsData.password,
          password_confirmation: credentialsData.password_confirmation,
          lots: credentialsData.lots,
          owner: credentialsData.owner,
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP returned ${response.status}`);
      }

      const result = await response.json();

      if (!result.status) {
        throw new Error(`Erreur returned ${response.message}`);
      }

      setData(result.user);
      setSuccess(true);

      return result.user;

    } catch (e) {
      setError(e.message);
      throw e;
    }
    finally {
      setLoading(false);
    }
  }, []);


  // Fonction pour mettre à jour des données existantes
  const updateCredentials = useCallback(async (id, credentialsData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { url, params } = initFetch(`extranet/user-credentials/${id}`, HttpMethod.PUT);
      const response = await fetch(url, {
        ...params,
        body: JSON.stringify({
          name: credentialsData.name,
          email: credentialsData.email,
          password: credentialsData.password || null,
          password_confirmation: credentialsData.password ? credentialsData.password_confirmation : null,
          lots: credentialsData.lots
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP returned ${response.status}`);
      }

      const result = await response.json();

      if (!result.status) {
        throw new Error(result.message || 'Erreur lors de la mise à jour');
      }

      setData(result.user);
      setSuccess(true);

      return result.user;
    }
    catch (e) {
      setError(e.message);
    }
    finally {
      setLoading(false);
    }

  },[]);


  // Fonction pour récupérer des données existantes (optionnel)
  const readCredentials = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const { url, params } = initFetch(`extranet/user-credentials/${id}`, HttpMethod.GET);
      const response = await fetch(url, params);

      if (!response.ok) {
        throw new Error(`Erreur HTTP returned ${response.status}`);
      }

      const result = await response.json();

      if (!result.status) {
        throw new Error(result.message || 'Erreur lors de la récupération');
      }

      const user = {...result.user, lots:result.lots};
      setData(user);

      return user;

    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);


  // Fonction pour réinitialiser les états
  const reset = useCallback(() => {
    setData({ name: '', email: '', lot_id: [] });
    setError(null);
    setSuccess(false);
  }, []);

  return {
    // Données
    data,

    // États
    loading,
    error,
    success,

    // Actions
    createCredentials,
    updateCredentials,
    readCredentials,
    reset
  };
}

export default useUserCredential;